import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import StatusBadge from '../components/StatusBadge';
import PageTitle from '../components/PageTitle';
import SetPricePanel from '../components/SetPricePanel';

const STATUSES = ['SUBMITTED','UNDER_REVIEW','ACTION_REQUIRED','DRAFT_SENT','APPROVED','FILED','ACKNOWLEDGED','REJECTED'];
const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Admin Dashboard ─────────────────────────────────────────────
export function AdminDashboard() {
  const [stats, setStats]         = useState(null);
  const [recent, setRecent]       = useState([]);
  const [enquiryStats, setEnquiryStats] = useState(null);
  const [gstStats, setGstStats]   = useState(null);
  const [dscStats, setDscStats]   = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/filing/stats'),
      api.get('/api/filing/all'),
      api.get('/api/enquiry/stats'),
      api.get('/api/gst/stats'),
      api.get('/api/dsc/stats'),
    ]).then(([s, f, eq, gst, dsc]) => {
      setStats(s.data);
      setRecent(f.data.slice(0, 8));
      setEnquiryStats(eq.data);
      setGstStats(gst.data);
      setDscStats(dsc.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="Admin Dashboard" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>CA Dashboard</h1>
        <p>Overview of all services and activity</p>
      </div>
      <div className="page-wide">

        {/* ITR Stats */}
        <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--gold)', fontWeight: 600, marginBottom: '.6rem' }}>ITR Filings</div>
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Filings',    value: stats?.total || 0,          icon: '📋' },
            { label: 'Awaiting Review',  value: stats?.submitted || 0,      icon: '📨', highlight: true },
            { label: 'Under Review',     value: stats?.underReview || 0,    icon: '🔍' },
            { label: 'Action Required',  value: stats?.actionRequired || 0, icon: '⚠️', highlight: true },
            { label: 'Filed',            value: stats?.filed || 0,          icon: '🏛' },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight && s.value > 0 ? { border: '2px solid var(--gold)' } : {}}>
              <div style={{ fontSize: '1.5rem', marginBottom: '.25rem' }}>{s.icon}</div>
              <div className="stat-num">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Enquiry + GST + DSC mini stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>

          <div className="card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)' }}>📞 Callback Enquiries</span>
              <Link to="/admin/enquiries"><button className="btn btn-outline btn-sm">View All →</button></Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
              {[['Total', enquiryStats?.total || 0, false], ['New', enquiryStats?.new || 0, true], ['Contacted', enquiryStats?.contacted || 0, false], ['Converted', enquiryStats?.converted || 0, false]].map(([l,v,h]) => (
                <div key={l} style={{ background: h && v > 0 ? '#FFF8E1' : 'var(--creamd)', border: h && v > 0 ? '1px solid var(--gold)' : 'none', borderRadius: 10, padding: '.75rem', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: h && v > 0 ? 'var(--gold)' : 'var(--navy)' }}>{v}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)' }}>🧾 GST Registrations</span>
              <Link to="/admin/gst"><button className="btn btn-outline btn-sm">View All →</button></Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
              {[['Total', gstStats?.total || 0, false], ['Pending', gstStats?.submitted || 0, true], ['Filed', gstStats?.filed || 0, false], ['Assigned', gstStats?.assigned || 0, false]].map(([l,v,h]) => (
                <div key={l} style={{ background: h && v > 0 ? '#FFF8E1' : 'var(--creamd)', border: h && v > 0 ? '1px solid var(--gold)' : 'none', borderRadius: 10, padding: '.75rem', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', color: h && v > 0 ? 'var(--gold)' : 'var(--navy)' }}>{v}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 600, fontSize: '.88rem', color: 'var(--navy)' }}>🔐 DSC Applications</span>
              <Link to="/admin/dsc"><button className="btn btn-outline btn-sm">View All →</button></Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '.5rem' }}>
              {[['Total', dscStats?.total || 0, false], ['New', dscStats?.applied || 0, true], ['Progress', dscStats?.inProgress || 0, false], ['Issued', dscStats?.issued || 0, false]].map(([l,v,h]) => (
                <div key={l} style={{ background: h && v > 0 ? '#FFF8E1' : 'var(--creamd)', border: h && v > 0 ? '1px solid var(--gold)' : 'none', borderRadius: 10, padding: '.6rem', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: h && v > 0 ? 'var(--gold)' : 'var(--navy)' }}>{v}</div>
                  <div style={{ fontSize: '.65rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent filings table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--creamd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--navy)' }}>Recent Filings</span>
            <Link to="/admin/filings"><button className="btn btn-outline btn-sm">View All →</button></Link>
          </div>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead><tr><th>#</th><th>Taxpayer</th><th>PAN</th><th>ITR</th><th>Year</th><th>Income</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {recent.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No filings yet</td></tr>}
                {recent.map(f => (
                  <tr key={f.id}>
                    <td style={{ color: 'var(--muted)' }}>#{f.id}</td>
                    <td><strong style={{ fontSize: '.88rem' }}>{f.fullName}</strong><br /><span style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{f.email}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>{f.pan}</td>
                    <td>{f.itrType}</td><td>AY {f.assessmentYr}</td>
                    <td>₹{Number(f.annualIncome || 0).toLocaleString('en-IN')}</td>
                    <td><StatusBadge status={f.status} /></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{new Date(f.submittedAt).toLocaleDateString('en-IN')}</td>
                    <td><Link to={`/admin/filings/${f.id}`}><button className="btn btn-primary btn-sm">Review</button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Admin All Filings ───────────────────────────────────────────
export function AdminFilings() {
  const [filings, setFilings]   = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get('/api/filing/all').then(r => { setFilings(r.data); setFiltered(r.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = filings;
    if (statusFilter !== 'ALL') list = list.filter(f => f.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(f => f.fullName?.toLowerCase().includes(q) || f.pan?.toLowerCase().includes(q) || f.email?.toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [statusFilter, search, filings]);

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="All Filings" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>All ITR Filings</h1>
        <p>{filings.length} total filings</p>
      </div>
      <div className="page-wide">
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search by name, PAN, email..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: 'var(--white)', outline: 'none' }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: 'var(--white)' }}>
            <option value="ALL">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{filtered.length} results</span>
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead><tr><th>#</th><th>Taxpayer</th><th>PAN</th><th>ITR</th><th>Year</th><th>Income</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No filings found</td></tr>}
                {filtered.map(f => (
                  <tr key={f.id}>
                    <td style={{ color: 'var(--muted)' }}>#{f.id}</td>
                    <td><strong style={{ fontSize: '.88rem' }}>{f.fullName}</strong><br /><span style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{f.email}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>{f.pan}</td>
                    <td>{f.itrType}</td><td>AY {f.assessmentYr}</td>
                    <td>₹{Number(f.annualIncome || 0).toLocaleString('en-IN')}</td>
                    <td><StatusBadge status={f.status} /></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{new Date(f.submittedAt).toLocaleDateString('en-IN')}</td>
                    <td><Link to={`/admin/filings/${f.id}`}><button className="btn btn-primary btn-sm">Review</button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Shared helpers ──────────────────────────────────────────────
function SingleDocDownload({ file }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${file}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.includes('_') ? file.substring(file.indexOf('_') + 1) : file;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); } finally { setBusy(false); }
  };
  const displayName = file.includes('_') ? file.substring(file.indexOf('_') + 1) : file;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.45rem .75rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8, marginTop: '.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', minWidth: 0, flex: 1 }}>
        <span>📄</span>
        <div style={{ minWidth: 0, flex: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontSize: '.74rem', color: 'var(--muted)' }}>
          {displayName}
        </div>
      </div>
      <button onClick={go} disabled={busy}
        style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.2rem .5rem', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', marginLeft: '.5rem' }}>
        {busy ? '...' : '⬇ Download'}
      </button>
    </div>
  );
}

function DocDownload({ label, filename }) {
  if (!filename) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--creamd)', borderRadius: 8, opacity: .5 }}>
      <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>— {label}</span>
      <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Not uploaded</span>
    </div>
  );

  const files = filename.split(',').filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
      <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--navy)' }}>{label}</div>
      {files.map(file => (
        <SingleDocDownload key={file} file={file} />
      ))}
    </div>
  );
}

function AdminUpload({ label, hint, onUpload, existing }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!file) return;
    setBusy(true);
    try { await onUpload(file); setFile(null); }
    catch (e) { alert(e.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ border: `1.5px solid ${existing ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, padding: '1rem', background: existing ? 'var(--greenl)' : 'var(--cream)' }}>
      <div style={{ fontWeight: 600, fontSize: '.82rem', color: existing ? 'var(--green)' : 'var(--navy)', marginBottom: '.25rem' }}>{existing ? '✅ ' : '📎 '}{label}</div>
      <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginBottom: existing ? 0 : '.75rem' }}>{existing ? 'Uploaded — user can download this' : hint}</div>
      {!existing && (
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} style={{ fontSize: '.78rem', flex: 1 }} />
          <button onClick={go} disabled={!file || busy}
            style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 7, padding: '.38rem .9rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {busy ? 'Uploading...' : '⬆ Upload'}
          </button>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
      <div style={{ color: 'var(--muted)', fontSize: '.72rem', marginBottom: '.1rem' }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{value}</div>
    </div>
  );
}

function TimelineRow({ icon, label, date }) {
  if (!date) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '.85rem', padding: '.4rem 0', borderBottom: '1px solid var(--creamd)' }}>
      <span>{icon}</span>
      <span style={{ color: 'var(--muted)', minWidth: 130 }}>{label}</span>
      <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{new Date(date).toLocaleString('en-IN')}</span>
    </div>
  );
}

// ── Admin Conversation Timeline ──────────────────────────────────
function AdminConversation({ filingId }) {
  const [comments, setComments] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get(`/api/filing/${filingId}/comments`)
      .then(r => setComments(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [filingId]);

  const send = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/filing/${filingId}/comments`, { message: newMsg });
      setNewMsg('');
      load();
    } catch (e) { alert('Failed to send'); }
    finally { setSending(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '1rem' }}><span className="spinner spinner-dark" /></div>;

  return (
    <div>
      <div style={{ maxHeight: 350, overflowY: 'auto', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {comments.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.82rem', padding: '1rem' }}>No messages yet</div>
        )}
        {comments.map(c => (
          <div key={c.id} style={{
            padding: '.6rem .9rem',
            borderRadius: 10,
            fontSize: '.82rem',
            background: c.senderType === 'SYSTEM' ? 'var(--creamd)' :
                         c.senderType === 'ADMIN'  ? 'var(--bluel)' : 'var(--greenl)',
            borderLeft: `3px solid ${c.senderType === 'SYSTEM' ? 'var(--muted)' :
                         c.senderType === 'ADMIN'  ? 'var(--blue)' : 'var(--green)'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
              <span style={{ fontWeight: 600, color: c.senderType === 'SYSTEM' ? 'var(--muted)' :
                             c.senderType === 'ADMIN' ? 'var(--blue)' : 'var(--green)', fontSize: '.72rem' }}>
                {c.senderType === 'SYSTEM' ? '🔄 System' : c.senderType === 'ADMIN' ? '👨‍💼 CA Team' : '👤 ' + c.senderName}
              </span>
              <span style={{ fontSize: '.68rem', color: 'var(--muted)' }}>
                {new Date(c.createdAt).toLocaleString('en-IN')}
              </span>
            </div>
            <div style={{ color: 'var(--text)' }}>{c.message}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '.5rem' }}>
        <input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message to the applicant..."
          style={{ flex: 1, padding: '.5rem .75rem', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'DM Sans, sans-serif', fontSize: '.82rem' }}
        />
        <button onClick={send} disabled={sending || !newMsg.trim()}
          style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, padding: '.5rem 1rem', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {sending ? '...' : '📨 Send'}
        </button>
      </div>
    </div>
  );
}

// ── Admin Filing Detail ─────────────────────────────────────────
export function AdminFilingDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [filing, setFiling]   = useState(null);
  const [status, setStatus]   = useState('');
  const [note, setNote]       = useState('');
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [showEmail, setShowEmail]       = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody]       = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    Promise.all([
      api.get(`/api/filing/${id}`),
      api.get(`/api/payment/for/ITR/${id}`).catch(() => ({ data: null })),
    ]).then(([f, p]) => {
      setFiling(f.data); setStatus(f.data.status); setNote(f.data.adminNote || '');
      setPayment(p.data || null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!filing) return;
    setEmailSubject(`Draft ITR Ready for Review — ${filing.itrType} AY ${filing.assessmentYr}`);
    setEmailBody(
      `Dear ${filing.fullName},\n\n` +
      `Your ITR (${filing.itrType}) for AY ${filing.assessmentYr} has been prepared.\n\n` +
      `Please log in to MyTaxNiti to review and approve your draft return.\n\n` +
      `Login: https://mytaxniti.com\n\n` +
      `Regards,\nMyTaxNiti CA Team`
    );
  }, [filing]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/filing/${id}/status`, { status, adminNote: note });
      setFiling(data);
      showMsg('success', `Status updated to ${status.replace(/_/g, ' ')}`);
    } catch (err) { showMsg('error', err.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  const sendDraftEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/api/filing/${id}/send-draft`, { subject: emailSubject, message: emailBody });
      setFiling(data); setStatus(data.status);
      setShowEmail(false);
      showMsg('success', `Email sent to ${filing.email} — status set to Draft Sent`);
    } catch (err) { showMsg('error', err.response?.data?.error || 'Email send failed'); }
    finally { setSendingEmail(false); }
  };

  const uploadDraftItr = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/api/filing/${id}/upload-draft-itr`, fd);
    setFiling(data); showMsg('success', 'Draft ITR uploaded');
  };

  const uploadItrV = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/api/filing/${id}/upload-itrv`, fd);
    setFiling(data); setStatus(data.status);
    showMsg('success', 'ITR-V uploaded — status set to Acknowledged');
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!filing)  return <div className="centered"><p>Filing not found.</p></div>;

  return (
    <>
      <PageTitle title={`Review Filing #${filing.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">Admin Review</div>
        <h1>Filing #{filing.id} — {filing.itrType}</h1>
        <p>{filing.fullName} · {filing.pan} · AY {filing.assessmentYr}</p>
      </div>
      <div className="page-wide">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/admin/filings')}>← Back</button>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>{msg.text}</div>}

        <div className="detail-grid">

          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            <div className="card">
              <SectionTitle>Personal Information</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Full Name"          value={filing.fullName} />
                <InfoRow label="PAN"                value={filing.pan} />
                <InfoRow label="Email"              value={filing.email} />
                <InfoRow label="Phone"              value={filing.phone} />
                <InfoRow label="Date of Birth"      value={filing.dob} />
                <InfoRow label="Residential Status" value={filing.residentialStatus} />
                <InfoRow label="Address"            value={filing.address} />
                <InfoRow label="Aadhar"             value={filing.aadhar} />
              </div>
            </div>

            <div className="card">
              <SectionTitle>Income Details</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Annual Income"  value={`₹${Number(filing.annualIncome || 0).toLocaleString('en-IN')}`} />
                <InfoRow label="TDS Paid"       value={`₹${Number(filing.taxPaid || 0).toLocaleString('en-IN')}`} />
                <InfoRow label="Deductions"     value={`₹${Number(filing.deductions || 0).toLocaleString('en-IN')}`} />
                <InfoRow label="Salary"         value={filing.salaryIncome   ? `₹${Number(filing.salaryIncome).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Business"       value={filing.businessIncome ? `₹${Number(filing.businessIncome).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Capital Gains"  value={filing.capitalGains   ? `₹${Number(filing.capitalGains).toLocaleString('en-IN')}` : null} />
                <InfoRow label="Bank"           value={filing.bankName} />
                <InfoRow label="Account No"     value={filing.accountNo} />
                <InfoRow label="IFSC"           value={filing.ifsc} />
              </div>
            </div>

            <div className="card">
              <SectionTitle>User Documents</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                <DocDownload label="Form 16"               filename={filing.docForm16} />
                <DocDownload label="AIS / 26AS"            filename={filing.docAis} />
                <DocDownload label="Bank Statement"        filename={filing.docBank} />
                <DocDownload label="Investment Proofs"     filename={filing.docInvestments} />
                <DocDownload label="Home Loan Certificate" filename={filing.docHomeloan} />
                <DocDownload label="Other Documents"       filename={filing.docOther} />
              </div>
              {filing.remarks && <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.82rem' }}>💬 {filing.remarks}</div>}
            </div>

            <div className="card">
              <SectionTitle>Upload Documents for User</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                <AdminUpload label="Draft ITR PDF" hint="Upload prepared draft for user to review"
                  existing={filing.docDraftItr} onUpload={uploadDraftItr} />
                <AdminUpload label="ITR-V Acknowledgement" hint="Upload after e-filing — sets status to Acknowledged"
                  existing={filing.docItrV} onUpload={uploadItrV} />
              </div>
            </div>

            <div className="card">
              <SectionTitle>Timeline</SectionTitle>
              <TimelineRow icon="📨" label="Submitted"      date={filing.submittedAt} />
              <TimelineRow icon="📋" label="Draft Sent"     date={filing.draftSentAt} />
              <TimelineRow icon="✅" label="User Approved"  date={filing.userApprovedAt} />
            </div>
          </div>

          {/* Right */}
          <div>
            <div style={{ position: 'sticky', top: '5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

              <div className="card">
                <SectionTitle>Send Draft to User</SectionTitle>
                <p style={{ fontSize: '.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                  Upload Draft ITR first, then notify the user to review it.
                </p>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowEmail(true)}>
                  📧 Send Draft Email
                </button>
                {filing.draftSentAt && (
                  <div style={{ fontSize: '.73rem', color: 'var(--muted)', marginTop: '.6rem', textAlign: 'center' }}>
                    Last sent: {new Date(filing.draftSentAt).toLocaleString('en-IN')}
                  </div>
                )}
              </div>

              <div className="card">
                <SectionTitle>Update Status</SectionTitle>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginBottom: '.35rem' }}>Current</div>
                  <StatusBadge status={filing.status} />
                </div>
                <div className="field" style={{ marginBottom: '1rem' }}>
                  <label>Set Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: '1.25rem' }}>
                  <label>Note to User</label>
                  <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Shown on user's progress tracker..." />
                </div>
                <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={saveStatus} disabled={saving}>
                  {saving ? <><span className="spinner" /> Saving...</> : '💾 Update Status'}
                </button>

                {/* ── Payment panel ── */}
                <SetPricePanel
                  serviceType="ITR"
                  serviceId={filing.id}
                  serviceDesc={`ITR Filing #${filing.id} — ${filing.itrType} AY ${filing.assessmentYr}`}
                  userId={filing.userId}
                  payment={payment}
                  onUpdated={p => setPayment(p)}
                />
              </div>

              <div className="card">
                <SectionTitle>CA Workflow</SectionTitle>
                {[
                  ['1', 'SUBMITTED',       '⬇ Download user documents'],
                  ['2', 'UNDER_REVIEW',    '🖥 Prepare ITR on portal'],
                  ['3', 'ACTION_REQUIRED', '⚠️ Request more info from user'],
                  ['4', 'DRAFT_SENT',      '⬆ Upload draft → send email'],
                  ['5', 'APPROVED',        '⏳ Await user approval'],
                  ['6', 'FILED',           '🏛 E-file on income tax portal'],
                  ['7', 'ACKNOWLEDGED',    '⬆ Upload ITR-V → done'],
                ].map(([n, s, desc]) => (
                  <div key={s} style={{ display: 'flex', gap: '.6rem', marginBottom: '.6rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: filing.status === s ? 'var(--green)' : 'var(--creamd)', color: filing.status === s ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{n}</div>
                    <div>
                      <div style={{ fontSize: '.75rem', fontWeight: 600, color: filing.status === s ? 'var(--green)' : 'var(--text)' }}>{s.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Conversation Timeline ── */}
              <div className="card">
                <SectionTitle>Conversation</SectionTitle>
                <AdminConversation filingId={id} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '2rem', width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--navy)' }}>📧 Send Draft Email</h3>
              <button onClick={() => setShowEmail(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>
            <div style={{ background: 'var(--bluel)', borderRadius: 8, padding: '.75rem 1rem', fontSize: '.8rem', color: 'var(--blue)', marginBottom: '1.25rem' }}>
              To: <strong>{filing.email}</strong>
            </div>
            <div className="field" style={{ marginBottom: '1rem' }}>
              <label>Subject</label>
              <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: '1.5rem' }}>
              <label>Message</label>
              <textarea rows={10} value={emailBody} onChange={e => setEmailBody(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '.82rem', lineHeight: 1.6 }} />
            </div>
            <div className="alert alert-info" style={{ fontSize: '.78rem', marginBottom: '1.25rem' }}>
              ℹ️ Status will be set to <strong>Draft Sent</strong> automatically.
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn btn-primary" onClick={sendDraftEmail} disabled={sendingEmail}
                style={{ flex: 1, justifyContent: 'center' }}>
                {sendingEmail ? <><span className="spinner" /> Sending...</> : '📧 Send Email'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowEmail(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}